import { ja } from '@/i18n/ja';
import type { NormalizedCaseNumber } from '@/lib/case-number/types';
import { Button } from './ui/Button';

interface Props {
  item: NormalizedCaseNumber;
  copied: boolean;
  onCopy: (item: NormalizedCaseNumber) => void;
}

/**
 * 事件番号 1 件を独立したカードで表示する。
 * カード全体もクリックでコピーできるが、日本のユーザーの操作予期と
 * アクセシビリティのため、明示的な「コピー」ボタンを必ず残す。
 */
export function ResultCard({ item, copied, onCopy }: Props) {
  return (
    <li className="flex flex-col gap-3 rounded-ln border border-line bg-white p-4 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1">
        <p className="ln-selectable break-all text-[19px] font-bold leading-snug tracking-wide text-ink">
          {item.normalized}
        </p>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink-subtle">
          {item.context_inferred && (
            <span className="inline-flex items-center rounded border border-brand-border bg-brand-subtle px-1.5 py-0.5 text-brand">
              {ja.results.contextInferred}
            </span>
          )}
          {item.occurrence_count > 1 && <span>入力中に{item.occurrence_count}回出現</span>}
          <span className="break-all">入力：{item.original_fragment}</span>
        </div>

        {item.warnings.includes('unknown_case_symbol') && (
          <p className="mt-2 rounded border border-[var(--ln-warning-border)] bg-[var(--ln-warning-bg)] px-2 py-1 text-[13px] text-[var(--ln-warning-ink)]">
            {ja.results.warningUnknownSymbol}
          </p>
        )}
        {item.warnings.includes('era_year_out_of_range') && (
          <p className="mt-2 rounded border border-[var(--ln-warning-border)] bg-[var(--ln-warning-bg)] px-2 py-1 text-[13px] text-[var(--ln-warning-ink)]">
            {ja.results.warningEraOutOfRange}
          </p>
        )}
      </div>

      <Button
        variant="secondary"
        className="shrink-0 self-start sm:self-auto"
        onClick={() => onCopy(item)}
        aria-label={ja.results.copyItemAriaLabel(item.normalized)}
      >
        {copied ? <span className="text-success">✓ {ja.actions.copied}</span> : ja.actions.copy}
      </Button>
    </li>
  );
}
